import React, { useRef, useState } from 'react';
import axios from 'axios';
import { BASE_URL } from '../Services/Calls';

export default function S3FileUpload({ onSuccess, width = '350', height = '150', accept = '*/*', multiple = false }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileCount, setFileCount] = useState(0);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  async function uploadOne(file, onProgress) {
    const token = localStorage.getItem('token');
    const { data } = await axios.post(
      `${BASE_URL}/upload/presigned-url`,
      { fileName: file.name, contentType: file.type || 'application/octet-stream' },
      { headers: { authorization: `Bearer ${token}` } },
    );

    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', data.presignedUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) onProgress(evt.loaded, evt.total);
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 PUT ${xhr.status}`)));
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(file);
    });

    return data.publicUrl;
  }

  async function handleFileChange(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setError(null);
    setUploading(true);
    setProgress(0);
    setFileCount(files.length);

    // Track per-file loaded bytes for aggregate progress
    const loaded = new Array(files.length).fill(0);
    const totals = files.map((f) => f.size);

    function onProgress(idx, bytes, total) {
      loaded[idx] = bytes;
      totals[idx] = total;
      const sumLoaded = loaded.reduce((a, b) => a + b, 0);
      const sumTotal = totals.reduce((a, b) => a + b, 0) || 1;
      setProgress(Math.round((sumLoaded * 100) / sumTotal));
    }

    try {
      const urls = await Promise.all(
        files.map((file, idx) => uploadOne(file, (bytes, total) => onProgress(idx, bytes, total))),
      );
      urls.forEach((url) => onSuccess(url));
    } catch (err) {
      setError('Upload failed. Please try again.');
      console.error('S3 upload error:', err);
    } finally {
      setUploading(false);
      setProgress(0);
      setFileCount(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const boxStyle = {
    width: `${width}px`,
    minHeight: `${height}px`,
    border: '2px dashed #cbd5e0',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '16px',
    cursor: uploading ? 'not-allowed' : 'pointer',
    background: uploading ? '#f7fafc' : '#fff',
  };

  return (
    <div style={boxStyle} onClick={() => !uploading && inputRef.current?.click()}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={uploading}
      />
      {uploading ? (
        <>
          <span style={{ fontSize: '14px', color: '#4a5568' }}>
            Uploading{fileCount > 1 ? ` ${fileCount} files` : ''}… {progress}%
          </span>
          <div style={{ width: '80%', background: '#e2e8f0', borderRadius: '4px', height: '6px' }}>
            <div style={{ width: `${progress}%`, background: '#4299e1', borderRadius: '4px', height: '6px', transition: 'width 0.2s' }} />
          </div>
        </>
      ) : (
        <>
          <span style={{ fontSize: '24px' }}>☁️</span>
          <span style={{ fontSize: '14px', color: '#4a5568' }}>
            {multiple ? 'Click to upload files' : 'Click to upload file'}
          </span>
          {error && <span style={{ fontSize: '12px', color: '#e53e3e' }}>{error}</span>}
        </>
      )}
    </div>
  );
}
